import { useCallback, useEffect, useMemo } from "react"

import { useInfiniteQuery } from "@tanstack/react-query"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { time } from "@planningcenter/datetime-fmt"
import { useApiRead } from "source/shared/SessionApiResource"
import { useConfiguration as useDateTimeConfiguration } from "source/shared/DatetimeFmt"

import calendarEventDetails from "source/calendar/utils/calendarEventDetails"
import { useDayjsInTimeZone } from "source/calendar/hooks/useDayjsInTimeZone"
import { groupBy, sortBy, filter, every } from "lodash/collection"
import { uniqWith } from "lodash/array"
import { cloneDeep, isEmpty } from "lodash/lang"
import { mapValues } from "lodash/object"
import { minBy } from "lodash"
import { useLocation } from "react-router"

const MONTH_VIEW_FIELDS = [
  "all_day_event",
  "campus_tags",
  "category_tags",
  "ends_at",
  "event_group",
  "event_registration_url",
  "event_time",
  "featured",
  "id",
  "image_url",
  "multi_day_event",
  "name",
  "show_page_path",
  "starts_at",
]

export const GALLERY_VIEW_EVENT_FIELDS = [
  "all_day_event",
  "campus_tags",
  "category_tags",
  "ends_at",
  "event_group",
  "event_registration_url",
  "event_time",
  "featured",
  "id",
  "initial_starts_at",
  "image_url",
  "multi_day_event",
  "name",
  "recurrence_description",
  "show_page_path",
  "starts_at",
]

export const LOCATION_BASED_EVENT_INCLUDES =
  "location,event_registration_url,category_tags,campus_tags"

const LIST_VIEW_PARAMS = [
  `include=${LOCATION_BASED_EVENT_INCLUDES}`,
  "filter=upcoming",
  "per_page=50",
]

const GALLERY_VIEW_PARAMS = [
  `include=${LOCATION_BASED_EVENT_INCLUDES}`,
  "filter=upcoming,first_occurrence",
  `fields[Event]=${GALLERY_VIEW_EVENT_FIELDS.join()}`,
  "order=-featured,visible_starts_at",
  "per_page=25",
]

const MONTH_VIEW_PARAMS = [
  "include=event_registration_url,category_tags,campus_tags",
  `fields[Event]=${MONTH_VIEW_FIELDS.join()}`,
  "per_page=100",
]

const FEATURED_VIEW_PARAMS = [
  `include=${LOCATION_BASED_EVENT_INCLUDES}`,
  "filter=upcoming,first_occurrence",
  "order=visible_starts_at",
  "where[featured]=1",
  "per_page=25",
]

export function useCalendarEvents(startDate, viewingMonth, viewData) {
  const dayjsInTZ = useDayjsInTimeZone()
  const { search } = useLocation()
  const params = new URLSearchParams(search)

  const { isListView, isGalleryView, isMonthView, view } = viewData

  const endOfMonth = dayjsInTZ(viewingMonth).endOf("month")
  const startOfMonth = dayjsInTZ(viewingMonth).startOf("month")
  const paddedEndDate = endOfMonth.endOf("week").endOf("day")
  const paddedStartDate = startOfMonth.startOf("week").startOf("day")

  const beginningDate = isMonthView ? paddedStartDate : startDate

  const dateTimeConfiguration = useDateTimeConfiguration()

  const categories = useApiRead("/calendar/v2/category_tags?per_page=100")
  const categoryParam = decodeURIComponent(params.get("category")).trim()
  const category =
    categories.data &&
    categories.data.find((cat) =>
      [cat.attributes.name.trim(), cat.id].includes(categoryParam),
    )

  const campuses = useApiRead("/calendar/v2/campus_tags?per_page=100")
  const campusParam = decodeURIComponent(params.get("campus")).trim()
  const campus =
    campuses.data &&
    campuses.data.find((cam) =>
      [cam.attributes.name.trim(), cam.id].includes(campusParam),
    )

  const eventParams = () => {
    const filter = []

    if (!isEmpty(category)) {
      filter.push(`where[category]=${encodeURIComponent(category.id)}`)
    }

    if (!isEmpty(campus)) {
      filter.push(`where[campus]=${encodeURIComponent(campus.id)},0`)
    }

    if (isListView) {
      return LIST_VIEW_PARAMS.concat(filter)
    } else if (isGalleryView) {
      return GALLERY_VIEW_PARAMS.concat(filter)
    } else if (view === "featured") {
      return FEATURED_VIEW_PARAMS.concat(filter)
    } else {
      filter.push(
        `where[starts_at][lte]=${paddedEndDate.toISOString()}&where[ends_at][gte]=${paddedStartDate.toISOString()}`,
      )
      return MONTH_VIEW_PARAMS.concat(filter)
    }
  }

  const initialDataUrl = ["/calendar/v2/events", eventParams().join("&")].join(
    "?",
  )

  const {
    data: pageData,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["calendar-events", initialDataUrl],
    queryFn: ({ pageParam }) => sessionApiClient.get(pageParam),
    initialPageParam: initialDataUrl,
    getNextPageParam: (lastPage) => lastPage?.links?.next ?? undefined,
  })

  const events = useMemo(() => {
    const raw = pageData?.pages.flatMap((page) => page.data ?? []) ?? []
    // Month view rendering assumes events are sorted by starts_at.
    return sortBy(raw, (event) => event.attributes.starts_at)
  }, [pageData])

  const included = useMemo(() => {
    const raw = pageData?.pages.flatMap((page) => page.included ?? []) ?? []
    return uniqWith(raw, (a, b) => a.type === b.type && a.id === b.id)
  }, [pageData])

  const lastPage = pageData?.pages[pageData.pages.length - 1]
  const hasMore = !!hasNextPage

  const lastDateLoaded = useMemo(() => {
    const lastEvent = lastPage?.data?.[lastPage.data.length - 1]
    return lastEvent?.attributes.starts_at ?? beginningDate.toISOString()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage, beginningDate.toISOString()])
  const lastDateLoadedDayjs = useMemo(
    () => dayjsInTZ(lastDateLoaded),
    [dayjsInTZ, lastDateLoaded],
  )

  const loadingEvents = isFetching || isFetchingNextPage

  const loadMore = useCallback(() => fetchNextPage(), [fetchNextPage])

  // return all events needed to render list view or month view. Mainly speeds up month view rendering.
  const eventsForView = useMemo(() => {
    return filter(events, (event) => {
      const filters = []
      if (isMonthView) {
        // where[starts_at][lte]=${paddedEndDate.toISOString()}&where[ends_at][gte]=${paddedStartDate.toISOString()}`,
        filters.push(
          !dayjsInTZ(event.attributes.starts_at).isAfter(paddedEndDate) &&
            !dayjsInTZ(event.attributes.ends_at).isBefore(beginningDate),
        )
      } else if (isGalleryView || view === "featured") {
        filters.push(
          !dayjsInTZ(event.attributes.ends_at).isBefore(beginningDate),
        )
      } else if (isListView) {
        filters.push(
          !dayjsInTZ(event.attributes.ends_at).isBefore(beginningDate) &&
            !dayjsInTZ(event.attributes.starts_at).isAfter(lastDateLoadedDayjs),
        )
      }

      if (view === "featured") {
        filters.push(event.attributes.featured)
      }

      if (!isEmpty(campus)) {
        filters.push(
          event.relationships.campus_tags.data
            .map((it) => it.id)
            .includes(campus.id.toString()) ||
            event.relationships.campus_tags.data.length == 0,
        )
      }

      if (!isEmpty(category)) {
        filters.push(
          event.relationships.category_tags.data
            .map((it) => it.id)
            .includes(category.id.toString()),
        )
      }

      return every(filters)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    events,
    isMonthView,
    isListView,
    isGalleryView,
    viewingMonth,
    beginningDate.toISOString(),
    paddedEndDate.toISOString(),
    paddedStartDate.toISOString(),
    campus,
    category,
    view,
    lastDateLoaded,
  ])

  // Month view fetches everything for the visible range; list/gallery
  // keep paging until the last loaded event reaches paddedEndDate.
  const paddedEndIso = paddedEndDate.toISOString()
  useEffect(() => {
    if (!hasNextPage || isFetching || isFetchingNextPage) return
    if (eventsForView.length === 0) return

    const shouldFetchMore =
      isMonthView || lastDateLoadedDayjs.isBefore(paddedEndIso)
    if (shouldFetchMore) fetchNextPage()
  }, [
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    eventsForView.length,
    isMonthView,
    lastDateLoadedDayjs,
    paddedEndIso,
    fetchNextPage,
  ])

  /**
   * Multi-day events should show on every day they occur. Let's add
   * each day that an event occurs into our list as its own entry,
   * so grouping by day Just Works.
   */
  const eventListWithEntryForEachDayOfEvent = (events) => {
    let extendedEventList = []
    events.forEach((event) => {
      if (isMultiDay(event)) {
        const eventSplitByDays = splitMultiDayEventByDay(event)
        const visibleEventDays = eventSplitByDays.filter(afterStartDate)

        extendedEventList.push(...visibleEventDays)
      } else {
        extendedEventList.push(event)
      }
    })

    return extendedEventList
  }

  const afterStartDate = (event) => {
    const { starts_at: startsAt } = event.attributes
    return (
      dayjsInTZ(startsAt).isSame(beginningDate, "day") ||
      dayjsInTZ(startsAt).isAfter(beginningDate, "day")
    )
  }

  const isMultiDay = (event) => event.attributes.multi_day_event

  const numDaysToDisplay = (event) => {
    const { ends_at: endsAt, starts_at: startsAt } = event.attributes
    const agendaEventThatEndsAfterLastDateLoaded =
      !isMonthView && dayjsInTZ(lastDateLoaded).isBefore(endsAt)

    const lastDateToDisplay =
      agendaEventThatEndsAfterLastDateLoaded && hasMore // only limit to the lastDateLoaded if there are more events to load
        ? dayjsInTZ(lastDateLoaded)
        : dayjsInTZ(endsAt)
    return Math.max(daysInRange(startsAt, lastDateToDisplay, dayjsInTZ), 1)
  }

  const splitMultiDayEventByDay = (event) => {
    const { starts_at: startsAt } = event.attributes

    const splitEvent = [...Array(numDaysToDisplay(event))].map((_, i) => {
      const newStartsAt = dayjsInTZ(startsAt).add(i, "day")
      const newEvent = cloneDeep(event)
      newEvent.attributes.initial_starts_at = dayjsInTZ(startsAt).toISOString()
      newEvent.attributes.starts_at = newStartsAt.toISOString()

      return newEvent
    })

    return splitEvent
  }

  const firstOccurrences = useMemo(() => {
    const grouped = groupBy(
      eventsForView,
      (event) => event.attributes.event_group,
    )
    return Object.values(
      mapValues(grouped, (events) =>
        minBy(events, (event) => new Date(event.attributes.ends_at)),
      ),
    )
  }, [eventsForView])

  const eventsWithDetails = useMemo(() => {
    const filteredEvents =
      isGalleryView || view === "featured"
        ? firstOccurrences
        : eventListWithEntryForEachDayOfEvent(eventsForView)
    const unsortedEvents = filteredEvents.map((event) => {
      const eventDetails = calendarEventDetails({ data: event, included })

      eventDetails.eventTime = getListEventTime({
        dateTimeConfiguration,
        startsAt: eventDetails.initialStartsAt
          ? dayjsInTZ(eventDetails.initialStartsAt)
          : dayjsInTZ(eventDetails.startsAt),
        endsAt: dayjsInTZ(eventDetails.endsAt),
        isAllDay: !!eventDetails.allDayEvent,
        isMultiDay: !!eventDetails.multiDayEvent,
        forDay: eventDetails.initialStartsAt ? eventDetails.startsAt : null,
        dayjsInTZ,
      })
      return eventDetails
    })
    return sortBy(unsortedEvents, [
      (event) => !event.featured,
      "startsAt",
      "endsAt",
      "name",
    ])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsForView, isGalleryView, view, firstOccurrences, included])

  const eventsByDay = useMemo(() => {
    return mapValues(
      groupBy(eventsWithDetails, (event) =>
        dayjsInTZ(event.startsAt).startOf("day").toISOString(),
      ),
      (event) => sortBy(event, ["startsAt", "endsAt", "name"]),
    )
  }, [eventsWithDetails])

  return {
    campus,
    campuses,
    categories,
    category,
    loadingEvents,
    eventsByDay,
    eventsWithDetails,
    hasMore,
    loadMore,
    lastDateLoaded,
  }
}

export function getListEventTime({
  dateTimeConfiguration,
  startsAt,
  endsAt,
  forDay = null,
  isAllDay = false,
  isMultiDay = false,
  dayjsInTZ,
}) {
  if (isAllDay) return "All day"

  if (isMultiDay) {
    if (dayjsInTZ(startsAt).isSame(dayjsInTZ(forDay), "day")) {
      return `Starts ${time(startsAt, dateTimeConfiguration)}`
    } else if (dayjsInTZ(endsAt).isSame(dayjsInTZ(forDay), "day")) {
      return `Ends ${time(endsAt, dateTimeConfiguration)}`
    } else {
      return "All day"
    }
  } else {
    return time(startsAt, endsAt, dateTimeConfiguration)
  }
}

function daysInRange(beginningDate, endDate, dayjsInTZ) {
  const beginningOfStartDate = dayjsInTZ(beginningDate).startOf("day")
  const beginningOfEndDate = dayjsInTZ(endDate).startOf("day")

  return beginningOfEndDate.diff(beginningOfStartDate, "day") + 1
}

export { daysInRange as testDaysInRange }
